import { Reducer } from 'redux';
import { languages } from '../../helpers/util';
import { createCallTypes } from '../../helpers/apiHelper';
import { User, Payment, General, Subscription, ActivePrescription } from '../../models';
import { Currency, GroupTypesB2C, Period, Status } from '../../helpers/product';
import { modalTypes } from './modal';

export interface AppState {
  loading: boolean;
  language: string;
  user: any;
  general: General;
  payment: Payment;
  subscription: Subscription;
  prescription: ActivePrescription;
  country: string;
  currency: Currency;
}

const storePrefix = 'APP';
export const appTypes = {
  RESET_STATE: `${storePrefix}/RESET_STATE`,
  CHANGE_LANGUAGE: `${storePrefix}/CHANGE_LANGUAGE`,
  SWITCH_PERIOD: `${storePrefix}/SWITCH_PERIOD`,
  SWITCH_ERROR: `${storePrefix}/SWITCH_ERROR`,
  ENABLE_AUTO_RENEWAL: `${storePrefix}/ENABLE_AUTO_RENEWAL`,
  LOAD_USER: createCallTypes(storePrefix, 'LOAD_USER'),
  LOAD_SUBSCRIPTIONS: createCallTypes(storePrefix, 'LOAD_SUBSCRIPTIONS'),
  START_FREE_TRIAL: createCallTypes(storePrefix, 'START_FREE_TRIAL'),
  START_PAYMENT: createCallTypes(storePrefix, 'START_PAYMENT'),
  UPDATE_PAYMENT_METHOD: createCallTypes(storePrefix, 'UPDATE_PAYMENT_METHOD'),
  START_OTHER_PAYMENT_METHOD: createCallTypes(storePrefix, 'START_OTHER_PAYMENT_METHOD'),
  UPGRADE_SUBSCRIPTION: createCallTypes(storePrefix, 'UPGRADE_SUBSCRIPTION'),
  DOWNGRADE_SUBSCRIPTION: createCallTypes(storePrefix, 'DOWNGRADE_SUBSCRIPTION'),
  CANCEL_SUBSCRIPTION: createCallTypes(storePrefix, 'CANCEL_SUBSCRIPTION'),
  CANCEL_DOWNGRADE: createCallTypes(storePrefix, 'CANCEL_DOWNGRADE'),
  CHANGE_PERIOD_SUBSCRIPTION: createCallTypes(storePrefix, 'CHANGE_PERIOD_SUBSCRIPTION'),
  START_ENABLE_SUBSCRIPTION: createCallTypes(storePrefix, 'START_ENABLE_SUBSCRIPTION'),
  ENABLE_SUBSCRIPTION: createCallTypes(storePrefix, 'ENABLE_SUBSCRIPTION'),
};

export const initialState: AppState = {
  language: languages[navigator.language.split(/[-_]/)[0]] ? navigator.language.split(/[-_]/)[0] : 'en',
  loading: true,
  user: undefined,
  general: undefined,
  payment: {
    isBusy: false,
    validationError: '',
    cardNameError: false,
    agreementError: false,
    autoRenewal: false,
  },
  subscription: {
    billingPeriod: Period.MONTHLY,
    plan: GroupTypesB2C.ESSENTIAL,
    status: Status.INACTIVE,
    nextPaymentTimestamp: -1,
  },
  prescription: undefined,
  country: undefined,
  currency: Currency.DOLLAR,
};

export const appReducer: Reducer<AppState> = (state = initialState, action) => {
  const { type, payload } = action;
  switch (type) {
    case appTypes.RESET_STATE:
      return initialState;
    case appTypes.LOAD_USER.REQUEST:
    case appTypes.LOAD_SUBSCRIPTIONS.REQUEST:
      return { ...initialState, language: state.language, country: undefined, currency: Currency.DOLLAR };
    case appTypes.LOAD_USER.SUCCESS:
      return { ...state, loading: false, user: payload.user, general: payload.general, country: payload.country, currency: payload.currency };
    case appTypes.LOAD_SUBSCRIPTIONS.SUCCESS:
      return { ...state,
        loading: false,
        user: payload.user,
        general: payload.general,
        subscription: { ...state.subscription, ...payload.subscription },
        prescription: payload.prescription,
        country: payload.country,
        currency: payload.currency };
    case appTypes.LOAD_USER.FAILURE:
    case appTypes.LOAD_SUBSCRIPTIONS.FAILURE:
      return { ...state, loading: false, general: payload.general, country: payload.country, currency: payload.currency };
    case appTypes.CHANGE_LANGUAGE:
      return { ...state, language: payload.language };
    case appTypes.SWITCH_PERIOD:
      return { ...state,
        general: { ...state.general,
          selectedPeriod: state.general.selectedPeriod === Period.MONTHLY ? Period.YEARLY : Period.MONTHLY } };
    case appTypes.SWITCH_ERROR:
      return { ...state, payment: { ...state.payment, [payload.errorName]: payload.error } };
    case appTypes.ENABLE_AUTO_RENEWAL:
      return { ...state, payment: { ...state.payment, autoRenewal: true } };
    case appTypes.START_FREE_TRIAL.SUCCESS:
      return { ...state, user: { ...state.user, freeTrial: undefined } };
    case appTypes.START_PAYMENT.REQUEST:
    case appTypes.UPDATE_PAYMENT_METHOD.REQUEST:
      return { ...state, payment: { ...state.payment, isBusy: true, validationError: undefined, cardNameError: false, agreementError: false } };
    case appTypes.START_PAYMENT.SUCCESS:
      return { ...state,
        loading: true,
        payment: { ...state.payment, isBusy: false, validationError: undefined, cardNameError: false, agreementError: false, autoRenewal: false } };
    case modalTypes.INPUT_CARD_DETAILS:
      return { ...state,
        payment: { ...state.payment, isBusy: false, validationError: undefined, cardNameError: false, agreementError: false, autoRenewal: false } };
    case appTypes.START_PAYMENT.FAILURE:
    case appTypes.UPDATE_PAYMENT_METHOD.FAILURE:
      return { ...state, payment: { ...state.payment, isBusy: false, ...payload } };
    case appTypes.UPDATE_PAYMENT_METHOD.SUCCESS:
      return { ...state, user: { ...state.user, paymentMethod: payload.paymentMethod } };
    case modalTypes.DOWNGRADE_SUBSCRIPTION:
    case modalTypes.UPGRADE_SUBSCRIPTION:
      return { ...state,
        general: { ...state.general,
          selectedPeriod: state.subscription.billingPeriod,
          selectedGroupType: state.subscription.plan } };
    case appTypes.UPGRADE_SUBSCRIPTION.SUCCESS:
    case appTypes.CHANGE_PERIOD_SUBSCRIPTION.SUCCESS:
      return { ...state,
        subscription: { ...state.subscription, billingPeriod: payload.period },
        general: { ...state.general, selectedPeriod: payload.period } };
    case appTypes.DOWNGRADE_SUBSCRIPTION.SUCCESS:
    case appTypes.CANCEL_SUBSCRIPTION.SUCCESS:
    case appTypes.CANCEL_DOWNGRADE.SUCCESS:
    case appTypes.ENABLE_SUBSCRIPTION.SUCCESS:
      return { ...state,
        subscription: { ...state.subscription, billingPeriod: payload.period, status: payload.status, plan: payload.plan },
        general: { ...state.general, selectedPeriod: payload.period } };
    case appTypes.START_ENABLE_SUBSCRIPTION.REQUEST:
      return { ...state, payment: { ...state.payment, agreementError: false } };
    case appTypes.START_ENABLE_SUBSCRIPTION.SUCCESS:
      return { ...state, general: { ...state.general, selectedPeriod: payload.period } };
    case appTypes.START_ENABLE_SUBSCRIPTION.FAILURE:
      return { ...state, payment: { ...state.payment, ...payload } };
    default:
      return state;
  }
};

export const appActions = {
  appReset: () => ({ type: appTypes.RESET_STATE }),
  changeLanguage: (language: string) => ({ type: appTypes.CHANGE_LANGUAGE, payload: { language } }),
  switchPeriod: () => ({ type: appTypes.SWITCH_PERIOD }),
  switchError: (errorName: string, error: boolean) => ({ type: appTypes.SWITCH_ERROR, payload: { errorName, error } }),
  enableAutoRenewal: () => ({ type: appTypes.ENABLE_AUTO_RENEWAL }),
  loadUser: () => ({ type: appTypes.LOAD_USER.REQUEST }),
  loadUserSuccess: (user: User, general: General, country: string, currency: Currency) => ({
    type: appTypes.LOAD_USER.SUCCESS,
    payload: { user, general, country, currency },
  }),
  loadUserFailure: (general: General, country: string, currency: Currency) => ({ type: appTypes.LOAD_USER.FAILURE,
    payload: { general, country, currency } }),
  loadSubscriptions: () => ({ type: appTypes.LOAD_SUBSCRIPTIONS.REQUEST }),
  loadSubscriptionsSuccess:
  (user: User, general: General, subscription: Subscription, prescription: ActivePrescription, country: string, currency: Currency) => ({
    type: appTypes.LOAD_SUBSCRIPTIONS.SUCCESS,
    payload: { user, general, subscription, prescription, country, currency },
  }),
  loadSubscriptionsFailure: (country: string, currency: Currency) => ({
    type: appTypes.LOAD_SUBSCRIPTIONS.FAILURE,
    payload: { country, currency },
  }),
  startFreeTrial: () => ({ type: appTypes.START_FREE_TRIAL.REQUEST }),
  startFreeTrialSuccess: () => ({ type: appTypes.START_FREE_TRIAL.SUCCESS }),
  startFreeTrialFailure: () => ({ type: appTypes.START_FREE_TRIAL.FAILURE }),
  startPayment: (cardName: string, stripe: any, elements: any, agreement: boolean) => ({ type: appTypes.START_PAYMENT.REQUEST,
    payload: { cardName, stripe, elements, agreement } }),
  startPaymentSuccess: () => ({ type: appTypes.START_PAYMENT.SUCCESS }),
  startPaymentFailure: (validationError: string = undefined, cardNameError = false, agreementError = false) => ({
    type: appTypes.START_PAYMENT.FAILURE,
    payload: { validationError, cardNameError, agreementError },
  }),
  updatePaymentMethod: (cardName: string, stripe: any, elements: any) => ({ type: appTypes.UPDATE_PAYMENT_METHOD.REQUEST,
    payload: { cardName, stripe, elements } }),
  updatePaymentMethodSuccess: (paymentMethod: any) => ({ type: appTypes.UPDATE_PAYMENT_METHOD.SUCCESS, payload: { paymentMethod } }),
  updatePaymentMethodFailure: (validationError: string = undefined, cardNameError = false) => ({
    type: appTypes.UPDATE_PAYMENT_METHOD.FAILURE,
    payload: { validationError, cardNameError },
  }),
  startOtherPaymentMethod: () => ({ type: appTypes.START_OTHER_PAYMENT_METHOD.REQUEST }),
  startOtherPaymentMethodSuccess: () => ({ type: appTypes.START_OTHER_PAYMENT_METHOD.SUCCESS }),
  startOtherPaymentMethodFailure: () => ({ type: appTypes.START_OTHER_PAYMENT_METHOD.FAILURE }),
  upgradeSubscription: () => ({ type: appTypes.UPGRADE_SUBSCRIPTION.REQUEST }),
  upgradeSubscriptionSuccess: (period: Period) => ({ type: appTypes.UPGRADE_SUBSCRIPTION.SUCCESS, payload: { period } }),
  upgradeSubscriptionFailure: () => ({ type: appTypes.UPGRADE_SUBSCRIPTION.FAILURE }),
  downgradeSubscription: () => ({ type: appTypes.DOWNGRADE_SUBSCRIPTION.REQUEST }),
  downgradeSubscriptionSuccess: (period: Period, status: Status, plan: GroupTypesB2C) => ({ type: appTypes.DOWNGRADE_SUBSCRIPTION.SUCCESS,
    payload: { period, status, plan } }),
  downgradeSubscriptionFailure: () => ({ type: appTypes.DOWNGRADE_SUBSCRIPTION.FAILURE }),
  cancelSubscription: () => ({ type: appTypes.CANCEL_SUBSCRIPTION.REQUEST }),
  cancelSubscriptionSuccess: (period: Period, status: Status, plan: GroupTypesB2C) => ({ type: appTypes.CANCEL_SUBSCRIPTION.SUCCESS,
    payload: { period, status, plan } }),
  cancelSubscriptionFailure: () => ({ type: appTypes.CANCEL_SUBSCRIPTION.FAILURE }),
  cancelDowngrade: () => ({ type: appTypes.CANCEL_DOWNGRADE.REQUEST }),
  cancelDowngradeSuccess: (period: Period, status: Status, plan: GroupTypesB2C) => ({ type: appTypes.CANCEL_DOWNGRADE.SUCCESS,
    payload: { period, status, plan } }),
  cancelDowngradeFailure: () => ({ type: appTypes.CANCEL_DOWNGRADE.FAILURE }),
  changePeriodSubscription: () => ({ type: appTypes.CHANGE_PERIOD_SUBSCRIPTION.REQUEST }),
  changePeriodSubscriptionSuccess: (period: Period) => ({ type: appTypes.CHANGE_PERIOD_SUBSCRIPTION.SUCCESS, payload: { period } }),
  changePeriodSubscriptionFailure: () => ({ type: appTypes.CHANGE_PERIOD_SUBSCRIPTION.FAILURE }),
  startEnableSubscription: (period: Period, agreement: boolean) => ({ type: appTypes.START_ENABLE_SUBSCRIPTION.REQUEST,
    payload: { period, agreement } }),
  startEnableSubscriptionSuccess: (period: Period) => ({ type: appTypes.START_ENABLE_SUBSCRIPTION.SUCCESS,
    payload: { period } }),
  startEnableSubscriptionFailure: (agreementError = false) => ({
    type: appTypes.START_PAYMENT.FAILURE,
    payload: { agreementError },
  }),
  enableSubscription: () => ({ type: appTypes.ENABLE_SUBSCRIPTION.REQUEST }),
  enableSubscriptionSuccess: (period: Period, status: Status, plan: GroupTypesB2C) => ({ type: appTypes.ENABLE_SUBSCRIPTION.SUCCESS,
    payload: { period, status, plan } }),
  enableSubscriptionFailure: () => ({ type: appTypes.ENABLE_SUBSCRIPTION.FAILURE }),
};
